import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import {
  AssetState,
  ConfirmStashedBillingAddress,
  MSG_CURRENCY_CHANGE_ON_BILLING_ADDRESS_UPDATE,
  MSG_PRICE_CHANGE_ON_BILLING_ADDRESS_UPDATE,
  MSG_PRICE_UNCHANGED_ON_BILLING_ADDRESS_UPDATE,
  ProductState,
  ResetStashedBillingAddress,
  StashCountrySelectionForConfirmation,
  SubscriptionBillingState,
  SubscriptionPaymentState
} from '@platform-frontend/checkout';
import {
  BillingAddress,
  CountryState,
  LicenseType,
  MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING,
  PaymentMethod,
  PricingInformation,
  ProductCategory,
  ProductLocalization,
  SubscriptionDetails
} from '@platform-frontend/marketplace';
import { InclusiveActionFilter } from '@platform-frontend/ui-tools';
import { Navigate, ShowWarningNotification } from '@platform-frontend/util';
import { MatomoTracker } from 'ngx-matomo-client';
import { Observable, asyncScheduler, combineLatest } from 'rxjs';
import { observeOn, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'yat-update-confirmation',
  templateUrl: './update-confirmation.component.html',
  styleUrls: ['./update-confirmation.component.scss']
})
export class UpdateConfirmationComponent implements OnInit {
  constructor(private activatedRoute: ActivatedRoute, private store: Store, private matomoTracker: MatomoTracker) {}

  updateActionFilter = new InclusiveActionFilter(ConfirmStashedBillingAddress);

  @Select(ProductState.billingAddressRequired)
  billingAddressRequired$: Observable<boolean>;

  @Select(CountryState.countries)
  countries$: Observable<string[]>;

  @Select(AssetState.subscriptionDetails)
  subscriptionDetails$: Observable<SubscriptionDetails | null>;

  @Select(AssetState.outstandingTransactions)
  outstandingTransactions$: Observable<boolean | null>;

  @Select(AssetState.licenseType)
  licenseType$: Observable<LicenseType | null>;

  @Select(SubscriptionPaymentState.connectedPaymentMethod)
  connectedPaymentMethod$: Observable<PaymentMethod | null>;

  @Select(SubscriptionBillingState.selectedBillingAddress)
  selectedBillingAddress$: Observable<BillingAddress | null>;

  @Select(SubscriptionBillingState.selectedCountry)
  selectedCountry$: Observable<string | null>;

  @Select(SubscriptionBillingState.selectedPricingInformation)
  selectedPricingInformation$: Observable<PricingInformation | null>;

  @Select(ProductState.isLive)
  isLive$: Observable<boolean>;

  @Select(ProductState.productLocalization)
  productLocalization$: Observable<ProductLocalization | null>;

  @Select(ProductState.productCategory)
  productCategory$: Observable<ProductCategory | null>;

  @Select(ProductState.vendorName)
  vendorName$: Observable<string | null>;

  @Select(ProductState.productName)
  productName$: Observable<string | null>;

  @Select(ProductState.fixedFulfillmentCountry)
  fixedFulfillmentCountry$: Observable<boolean | null>;

  readonly MATOMO_EVENT_CATEGORY_BILLING = MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING;

  ngOnInit(): void {
    combineLatest([this.selectedPricingInformation$, this.subscriptionDetails$])
      .pipe(
        observeOn(asyncScheduler), // dispatch notification asynchronously to avoid "expression changed" error
        switchMap(([selectedPricingInformation, subscriptionDetails]) =>
          this.showWarningNotification(selectedPricingInformation, subscriptionDetails?.pricingInformation ?? null)
        ),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private showWarningNotification(
    selectedPricingInformation: PricingInformation | null,
    subscriptionPricingInformation: PricingInformation | null
  ): Observable<unknown> {
    if (selectedPricingInformation?.currency !== subscriptionPricingInformation?.currency) {
      return this.store.dispatch(new ShowWarningNotification(MSG_CURRENCY_CHANGE_ON_BILLING_ADDRESS_UPDATE));
    } else if (selectedPricingInformation?.total !== subscriptionPricingInformation?.total) {
      return this.store.dispatch(new ShowWarningNotification(MSG_PRICE_CHANGE_ON_BILLING_ADDRESS_UPDATE));
    } else {
      return this.store.dispatch(new ShowWarningNotification(MSG_PRICE_UNCHANGED_ON_BILLING_ADDRESS_UPDATE));
    }
  }

  tryConfirm(): void {
    this.store.dispatch(new ConfirmStashedBillingAddress(true));
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_BILLING, 'Confirm_BillingAddressForm');
  }

  cancel(): void {
    this.store.dispatch(new ResetStashedBillingAddress());
    this.store.dispatch(new Navigate(['../overview'], { relativeTo: this.activatedRoute }));
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_BILLING, 'Cancel_BillingAddressForm');
  }

  managePaymentMethod(): void {
    this.store.dispatch(new Navigate(['../payment-method-selection'], { relativeTo: this.activatedRoute }));
  }

  manageBillingAddress(): void {
    this.store.dispatch(new Navigate(['../billing-address-selection'], { relativeTo: this.activatedRoute }));
  }

  createBillingAddress(): void {
    this.store.dispatch(new Navigate(['../billing-address'], { relativeTo: this.activatedRoute }));
  }

  updateCountryForNoBillingAddressSelection(countryCode: string): void {
    this.store.dispatch(new StashCountrySelectionForConfirmation(countryCode));
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_BILLING, 'Modify_NoBillingAddressIfNoCountry', countryCode);
  }
}
